import { Autocomplete, TextField, FilterOptionsState, createFilterOptions } from "@mui/material";
import { findOptionKey } from "encamp-shared/src/constants/alternateIdTypes";
import { FieldValues, Control, Controller, FieldPath } from "react-hook-form";

type SelectWithFreeSoloProps<T extends FieldValues> = {
  name: FieldPath<T>;
  control: Control<T>;
  options: Record<string, string>;
  disabled?: boolean;
  label?: string;
};

export const SelectWithFreeSolo = <T extends FieldValues>({
  name,
  control,
  options,
  disabled,
  label = "Name",
}: SelectWithFreeSoloProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          {...field}
          freeSolo
          clearOnBlur
          options={Object.keys(options)}
          filterOptions={(keys, params) => filterOptions(keys, params, options)}
          renderInput={(params) => <TextField {...params} label={label} error={!!error} />}
          getOptionLabel={(option) => getOptionLabel(option, options)}
          onChange={(_, data) => {
            field.onChange(
              typeof data === "string" ? (findOptionKey(data, options) ?? data) : data
            );
          }}
          onBlur={() => field.onBlur()}
          disabled={disabled}
        />
      )}
    />
  );
};

function getOptionLabel(option: string, options: Record<string, string>) {
  if (Object.keys(options).includes(option)) {
    return options[option];
  }

  return option;
}

function filterOptions(
  keys: string[],
  params: FilterOptionsState<string>,
  options: Record<string, string>
): string[] {
  const filterFunction = createFilterOptions<string>();
  const filteredOptions = filterFunction(keys, params);
  const { inputValue } = params;
  // Suggest the creation of a new value
  const isExisting = findOptionKey(inputValue, options) !== null;
  if (inputValue !== "" && !isExisting) {
    filteredOptions.push(inputValue);
  }

  return filteredOptions;
}
